import { useEffect, useState } from "react";
import {
  useClassificacoesFiscais,
  useCstPisCofinsCombo,
  useCClassTribCombo,
  useCsosnCombo,
  useCstIcmsCombo,
  useOrigensCombo,
  useCstsIpiCombo,
  useNaturezasOperacaoCombo,
} from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { CfopSearchCombobox } from "@/components/CfopSearchCombobox";
import { CestSearchCombobox } from "@/components/CestSearchCombobox";
import { AnpSearchCombobox } from "@/components/AnpSearchCombobox";
import { correlacaoService } from "@/features/fiscal/services/fiscalService";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type {
  ClassificacaoFiscalListagemDto,
  ClassificacaoFiscalDto,
  ClassificacaoFiscalUfDto,
  SalvarClassificacaoFiscalRequest,
  SalvarClassificacaoFiscalUfRequest,
} from "@/features/fiscal/types";
import { NcmSearchCombobox } from "@/components/NcmSearchCombobox";
import { CClassTribSearchCombobox } from "@/components/CClassTribSearchCombobox";
import { NaturezaOperacaoSearchCombobox } from "@/components/NaturezaOperacaoSearchCombobox";
import { NaturezaReceitaSearchCombobox } from "@/components/NaturezaReceitaSearchCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ShieldCheck, Search } from "lucide-react";
import { toast } from "sonner";

// ── Constantes ─────────────────────────────────────────────────────────────

const UFS_BR = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

const MOTIVOS_DESONERACAO = [
  { value: "__none__",                              label: "Nenhum" },
  { value: "DEFICIENTE_CONDUTOR",                   label: "Deficiente condutor" },
  { value: "DEFICIENTE_NAO_CONDUTOR",               label: "Deficiente não condutor" },
  { value: "DIPLOMATICO_CONSULAR",                  label: "Diplomático/Consular" },
  { value: "FROTISTA_LOCADORA",                     label: "Frotista/Locadora" },
  { value: "OLIMPIADAS_RIO_2016",                   label: "Olimpíadas Rio 2016" },
  { value: "ORGAO_FOMENTO_DESENVOLV",               label: "Órgão de fomento e desenvolvim..." },
  { value: "OUTROS",                                label: "Outros" },
  { value: "PRODUTO_AGROPECUARIO",                  label: "Produto agropecuário" },
  { value: "SUFRAMA",                               label: "SUFRAMA" },
  { value: "TAXI",                                  label: "Táxi" },
  { value: "UTILIT_MOTOC_AMAZONIA_OCID",            label: "Utilit. e Motoc. da Amazônia Ocide..." },
  { value: "VENDA_ORGAOS_PUBLICOS",                 label: "Venda a Órgãos Públicos" },
];

// ── Estado inicial ─────────────────────────────────────────────────────────

const EMPTY_FORM: SalvarClassificacaoFiscalRequest = {
  ncmId: 0, codigoNcmComplemento: null, cClassTribId: 0,
  cestId: null, anpId: null, origemId: null,
  isMonofasico: false, destacarIcmsST: false, incideIpi: false,
  incidePisCofins: false, produtoEspecifico: false,
  cfopSaida: 0, cfopSaidaNaoContrib: 0, cfopSaidaUf: 0,
  cfopSaidaUfNaoContrib: 0, cfopSaidaExterior: 0, cfopSaidaContaForaConta: 0,
  cfopEntrada: 0, cfopEntradaUf: 0, cfopEntradaExterior: 0,
  cstPisCofinsEntradaId: null, cstPisCofinsaidasId: null, naturezaReceitaId: null,
  ipiCstEntradaId: null, ipiCstSaidaId: null, aliqIpi: null,
  aliqCbs: 0, aliqIbsUf: 0, aliqIbsMunicipio: 0,
  cstId: null, descAnp: null, codAnp: null, anpCodif: null,
  anpVPart: null, anpPGNi: null, anpPGNn: null, anpPGLP: null,
  perPisCofinsEntrada: null, perPisCofinsaida: null, codEnquadramentoIpi: null,
  percTributosInformado: null, observacao: null,
  itensUf: [],
};

const EMPTY_UF_ROW: SalvarClassificacaoFiscalUfRequest = {
  uf: null, modDeterminacaoBC: null, reducaoBcIcms: null, aliqIcms: null,
  bcOperacaoPropria: null, destacarIcms: false, modDeterminacaoBCSt: null,
  reducaoBcSt: null, aliqIcmsSt: null, margemValorSt: null,
  aliqIcmsAproveitado: null, tributacaoIcms: null, situacaoTributariaId: null,
  csosnId: null, precoBaseSubs: null, aliqCbs: 0, aliqIbsUf: 0,
  aliqIbsMunicipio: 0, motivoDesoneracao: null, cBnef: null, naturezaOperacaoId: null,
};

// ── Conversor DTO → Form ───────────────────────────────────────────────────

function dtoToForm(d: ClassificacaoFiscalDto): SalvarClassificacaoFiscalRequest {
  return {
    ncmId: d.ncmId, codigoNcmComplemento: d.codigoNcmComplemento,
    cClassTribId: d.cClassTribId, cestId: d.cestId, anpId: d.anpId, origemId: d.origemId,
    isMonofasico: d.isMonofasico, destacarIcmsST: d.destacarIcmsST,
    incideIpi: d.incideIpi, incidePisCofins: d.incidePisCofins, produtoEspecifico: d.produtoEspecifico,
    cfopSaida: d.cfopSaida, cfopSaidaNaoContrib: d.cfopSaidaNaoContrib,
    cfopSaidaUf: d.cfopSaidaUf, cfopSaidaUfNaoContrib: d.cfopSaidaUfNaoContrib,
    cfopSaidaExterior: d.cfopSaidaExterior, cfopSaidaContaForaConta: d.cfopSaidaContaForaConta,
    cfopEntrada: d.cfopEntrada, cfopEntradaUf: d.cfopEntradaUf, cfopEntradaExterior: d.cfopEntradaExterior,
    cstPisCofinsEntradaId: d.cstPisCofinsEntradaId, cstPisCofinsaidasId: d.cstPisCofinsaidasId,
    naturezaReceitaId: d.naturezaReceitaId, ipiCstEntradaId: d.ipiCstEntradaId,
    ipiCstSaidaId: d.ipiCstSaidaId, aliqIpi: d.aliqIpi,
    aliqCbs: d.aliqCbs, aliqIbsUf: d.aliqIbsUf, aliqIbsMunicipio: d.aliqIbsMunicipio,
    cstId: d.cstId, descAnp: d.descAnp, codAnp: d.codAnp, anpCodif: d.anpCodif,
    anpVPart: d.anpVPart, anpPGNi: d.anpPGNi, anpPGNn: d.anpPGNn, anpPGLP: d.anpPGLP,
    perPisCofinsEntrada: d.perPisCofinsEntrada, perPisCofinsaida: d.perPisCofinsaida,
    codEnquadramentoIpi: d.codEnquadramentoIpi,
    percTributosInformado: d.percTributosInformado, observacao: d.observacao,
    itensUf: d.itensUf.map(u => ({
      uf: u.uf, modDeterminacaoBC: u.modDeterminacaoBC, reducaoBcIcms: u.reducaoBcIcms,
      aliqIcms: u.aliqIcms, bcOperacaoPropria: u.bcOperacaoPropria, destacarIcms: u.destacarIcms,
      modDeterminacaoBCSt: u.modDeterminacaoBCSt, reducaoBcSt: u.reducaoBcSt,
      aliqIcmsSt: u.aliqIcmsSt, margemValorSt: u.margemValorSt,
      aliqIcmsAproveitado: u.aliqIcmsAproveitado, tributacaoIcms: u.tributacaoIcms,
      situacaoTributariaId: u.situacaoTributariaId, csosnId: u.csosnId,
      precoBaseSubs: u.precoBaseSubs, aliqCbs: u.aliqCbs, aliqIbsUf: u.aliqIbsUf,
      aliqIbsMunicipio: u.aliqIbsMunicipio, motivoDesoneracao: u.motivoDesoneracao, cBnef: u.cBnef,
      naturezaOperacaoId: u.naturezaOperacaoId,
    })),
  };
}

// ── Componentes auxiliares ─────────────────────────────────────────────────

function Num({ label, value, onChange }: {
  label: string; value: number | null; onChange: (v: number | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number" min={0} step={0.01} placeholder="0,00"
        className="h-8 text-sm"
        value={value ?? ""}
        onChange={e => { const v = parseFloat(e.target.value); onChange(isNaN(v) ? null : v); }}
      />
    </div>
  );
}

function CfopInput({ label, value, onChange }: {
  label: string; value: number; onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <CfopSearchCombobox value={value || null} onChange={v => onChange(v ?? 0)} />
    </div>
  );
}

// Modalidade de determinação da BC do ICMS (campo modBC da SEFAZ)
const MOD_BC = [
  { value: "0", label: "0 — Margem Valor Agregado (%)" },
  { value: "1", label: "1 — Pauta (valor)" },
  { value: "2", label: "2 — Preço Tabelado Máx. (valor)" },
  { value: "3", label: "3 — Valor da operação" },
];
const MOD_BC_ST = [
  { value: "0", label: "0 — Preço tabelado/máximo sugerido" },
  { value: "1", label: "1 — Lista Negativa (valor)" },
  { value: "2", label: "2 — Lista Positiva (valor)" },
  { value: "3", label: "3 — Lista Neutra (valor)" },
  { value: "4", label: "4 — Margem Valor Agregado (%)" },
  { value: "5", label: "5 — Pauta (valor)" },
];

// ── Dialog UF ─────────────────────────────────────────────────────────────

function UfDialog({
  open, row, csosnCombo, cstIcmsCombo, natOpCombo, onClose, onSave,
}: {
  open: boolean;
  row: SalvarClassificacaoFiscalUfRequest;
  csosnCombo: { id: number; codigoCsosn: number; descricao: string | null }[];
  cstIcmsCombo: { cst: string | null; descricao: string | null }[];
  natOpCombo: { id: number; descricao: string | null }[];
  onClose: () => void;
  onSave: (r: SalvarClassificacaoFiscalUfRequest) => void;
}) {
  const [f, setF] = useState(row);
  const set = <K extends keyof SalvarClassificacaoFiscalUfRequest>(k: K, v: SalvarClassificacaoFiscalUfRequest[K]) =>
    setF(prev => ({ ...prev, [k]: v }));

  // O pai abre o diálogo por `open` (onOpenChange não dispara nesse caso):
  // recarrega a linha a cada abertura, senão "Editar UF" vinha vazio/com a UF anterior.
  useEffect(() => { if (open) setF(row); }, [open, row]);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ICMS por UF</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">UF</Label>
            <Select value={f.uf ?? ""} onValueChange={v => set("uf", v && v !== "__none__" ? v : null)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {UFS_BR.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">CSOSN</Label>
            <Select value={f.csosnId?.toString() ?? ""} onValueChange={v => set("csosnId", v && v !== "__none__" ? parseInt(v) : null)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {csosnCombo.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.codigoCsosn} – {c.descricao}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1 col-span-2">
            <Label className="text-xs">Natureza da Operação</Label>
            <NaturezaOperacaoSearchCombobox
              value={f.naturezaOperacaoId}
              onChange={id => set("naturezaOperacaoId", id)}
              placeholder="Selecione"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">CST ICMS (Tributação)</Label>
            <Select value={f.tributacaoIcms ?? ""} onValueChange={v => set("tributacaoIcms", v && v !== "__none__" ? v : null)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {cstIcmsCombo.map(c => <SelectItem key={c.cst} value={c.cst ?? "?"}>{c.cst} — {c.descricao}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Mod. Det. BC ICMS</Label>
            <Select value={f.modDeterminacaoBC?.toString() ?? ""} onValueChange={v => set("modDeterminacaoBC", v === "__none__" ? null : parseInt(v))}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {MOD_BC.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Num label="Alíq. ICMS (%)" value={f.aliqIcms} onChange={v => set("aliqIcms", v)} />
          <Num label="Red. BC ICMS (%)" value={f.reducaoBcIcms} onChange={v => set("reducaoBcIcms", v)} />
          <Num label="BC Operação Própria (%)" value={f.bcOperacaoPropria} onChange={v => set("bcOperacaoPropria", v)} />

          <div className="space-y-1">
            <Label className="text-xs">Mod. Det. BC ST</Label>
            <Select value={f.modDeterminacaoBCSt?.toString() ?? ""} onValueChange={v => set("modDeterminacaoBCSt", v === "__none__" ? null : parseInt(v))}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {MOD_BC_ST.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Num label="Alíq. ICMS ST (%)" value={f.aliqIcmsSt} onChange={v => set("aliqIcmsSt", v)} />
          <Num label="Red. BC ST (%)" value={f.reducaoBcSt} onChange={v => set("reducaoBcSt", v)} />
          <Num label="Margem Valor ST (%)" value={f.margemValorSt} onChange={v => set("margemValorSt", v)} />
          <Num label="Alíq. ICMS Aproveitado (%)" value={f.aliqIcmsAproveitado} onChange={v => set("aliqIcmsAproveitado", v)} />
          <Num label="Preço Base Substituição" value={f.precoBaseSubs} onChange={v => set("precoBaseSubs", v)} />
          <Num label="Alíq. CBS (%)" value={f.aliqCbs} onChange={v => set("aliqCbs", v ?? 0)} />
          <Num label="Alíq. IBS UF (%)" value={f.aliqIbsUf} onChange={v => set("aliqIbsUf", v ?? 0)} />
          <Num label="Alíq. IBS Município (%)" value={f.aliqIbsMunicipio} onChange={v => set("aliqIbsMunicipio", v ?? 0)} />

          <div className="space-y-1">
            <Label className="text-xs">Motivo Desoneração</Label>
            <Select value={f.motivoDesoneracao ?? ""} onValueChange={v => set("motivoDesoneracao", v && v !== "__none__" ? v : null)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
              <SelectContent>
                {MOTIVOS_DESONERACAO.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">CBenef</Label>
            <Input className="h-8 text-sm" value={f.cBnef ?? ""} onChange={e => set("cBnef", e.target.value || null)} />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-1">
          <Switch id="destacar" checked={f.destacarIcms} onCheckedChange={v => set("destacarIcms", v)} />
          <Label htmlFor="destacar" className="text-sm">Destaca ICMS</Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave(f)}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Página principal ───────────────────────────────────────────────────────

export default function ClassificacaoFiscal() {
  const [busca,         setBusca]         = useState("");
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);

  const { query, obterPorId, criar, atualizar, excluir } = useClassificacoesFiscais({
    busca:         busca || undefined,
    pagina,
    tamanhoPagina,
  });
  const cstPisCombo     = useCstPisCofinsCombo();
  const cClassTribCombo = useCClassTribCombo();
  const csosnCombo      = useCsosnCombo();
  const cstIcmsCombo    = useCstIcmsCombo();
  const origensCombo    = useOrigensCombo();
  const cstsIpiCombo    = useCstsIpiCombo();
  const natOpCombo      = useNaturezasOperacaoCombo();
  const cstIcmsList     = (cstIcmsCombo.data ?? []).filter(c => c.cst && c.cst.length <= 2); // CST ICMS tradicional
  const cstsIpiList     = cstsIpiCombo.data ?? [];
  const origensList     = origensCombo.data ?? [];
  const natOpList       = natOpCombo.data ?? [];
  const cClassTribPorId = new Map((cClassTribCombo.data ?? []).map(c => [c.id, c]));

  const itens: ClassificacaoFiscalListagemDto[] = query.data?.itens ?? [];
  const totalItens   = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem   = Math.min(pagina * tamanhoPagina, totalItens);

  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [excluirId,   setExcluirId]   = useState<number | null>(null);
  const [editingId,   setEditingId]   = useState<number | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [form,        setForm]        = useState<SalvarClassificacaoFiscalRequest>(EMPTY_FORM);
  const [ncmLabel,    setNcmLabel]    = useState("");
  const [ufDialogOpen,  setUfDialogOpen]  = useState(false);
  const [ufEditingIdx,  setUfEditingIdx]  = useState<number | null>(null);
  const [ufEditingRow,  setUfEditingRow]  = useState<SalvarClassificacaoFiscalUfRequest>(EMPTY_UF_ROW);
  const [cstCorrelacao,   setCstCorrelacao]   = useState("");
  const [preenchendoCfops, setPreenchendoCfops] = useState(false);

  const set = <K extends keyof SalvarClassificacaoFiscalRequest>(k: K, v: SalvarClassificacaoFiscalRequest[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  // ── Abrir diálogo ──────────────────────────────────────────────────────

  const abrirNovo = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setNcmLabel("");
    setDialogOpen(true);
  };

  const abrirEditar = async (id: number) => {
    setLoading(true);
    try {
      const d: ClassificacaoFiscalDto = await obterPorId(id);
      setEditingId(id);
      setForm(dtoToForm(d));
      setNcmLabel("");
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Salvar ─────────────────────────────────────────────────────────────

  const handleSalvar = async () => {
    if (!form.ncmId) { toast.error("Selecione um NCM."); return; }
    if (!form.cClassTribId) { toast.error("Selecione a Classificação Tributária."); return; }
    setLoading(true);
    try {
      if (editingId) {
        await atualizar.mutateAsync({ id: editingId, req: form });
        toast.success("Classificação fiscal atualizada.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Classificação fiscal criada.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar classificação fiscal.");
    } finally {
      setLoading(false);
    }
  };

  // ── Excluir ────────────────────────────────────────────────────────────

  const handleExcluir = async () => {
    if (!excluirId) return;
    try {
      await excluir.mutateAsync(excluirId);
      toast.success("Classificação fiscal excluída.");
    } catch {
      toast.error("Erro ao excluir.");
    } finally {
      setExcluirId(null);
    }
  };

  // ── Itens UF ──────────────────────────────────────────────────────────

  const abrirUfNovo = () => {
    setUfEditingIdx(null);
    setUfEditingRow(EMPTY_UF_ROW);
    setUfDialogOpen(true);
  };

  const abrirUfEditar = (idx: number) => {
    setUfEditingIdx(idx);
    setUfEditingRow({ ...form.itensUf[idx] });
    setUfDialogOpen(true);
  };

  const salvarUf = (row: SalvarClassificacaoFiscalUfRequest) => {
    const novasUf = [...form.itensUf];
    if (ufEditingIdx !== null) novasUf[ufEditingIdx] = row;
    else novasUf.push(row);
    set("itensUf", novasUf);
    setUfDialogOpen(false);
  };

  const removerUf = (idx: number) => {
    set("itensUf", form.itensUf.filter((_, i) => i !== idx));
  };

  // Correlação padrão CFOP × CST — preenche os 9 CFOPs a partir do CST de saída
  const preencherCfopsPorCst = async () => {
    if (!cstCorrelacao) { toast.error("Selecione o CST de saída."); return; }
    setPreenchendoCfops(true);
    try {
      const c = await correlacaoService.obterPorCst(cstCorrelacao);
      setForm(f => ({
        ...f,
        cfopSaida: c.cfopSaida, cfopSaidaNaoContrib: c.cfopSaidaNaoContrib,
        cfopSaidaUf: c.cfopSaidaUf, cfopSaidaUfNaoContrib: c.cfopSaidaUfNaoContrib,
        cfopSaidaExterior: c.cfopSaidaExterior, cfopSaidaContaForaConta: c.cfopSaidaContaForaConta,
        cfopEntrada: c.cfopEntrada, cfopEntradaUf: c.cfopEntradaUf, cfopEntradaExterior: c.cfopEntradaExterior,
      }));
      toast.success(`CFOPs preenchidos pela correlação padrão do CST ${cstCorrelacao}.`);
    } catch {
      toast.error(`Sem correlação padrão cadastrada para o CST ${cstCorrelacao}.`);
    } finally {
      setPreenchendoCfops(false);
    }
  };

  const csosnComboData = csosnCombo.data ?? [];

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Classificação Fiscal
          </h1>
          <p className="text-muted-foreground">Gerencie as classificações fiscais por NCM</p>
        </div>
        <Button onClick={abrirNovo}>
          <Plus className="h-4 w-4 mr-2" /> Nova
        </Button>
      </div>

      {/* Busca */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por NCM ou descrição..."
          className="pl-9"
          value={busca}
          onChange={e => { setBusca(e.target.value); setPagina(1); }}
        />
      </div>

      {/* Tabela listagem */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>NCM</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Compl.</TableHead>
              <TableHead>Cl. Trib.</TableHead>
              <TableHead>PIS/COFINS</TableHead>
              <TableHead>IPI</TableHead>
              <TableHead>Monofásico</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Carregando...</TableCell></TableRow>
            )}
            {!query.isLoading && itens.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nenhum registro encontrado.</TableCell></TableRow>
            )}
            {itens.map(item => (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-sm">{item.ncmCodigo}</TableCell>
                <TableCell className="text-sm max-w-48 truncate">{item.ncmDescricao}</TableCell>
                <TableCell className="text-sm">{item.codigoNcmComplemento}</TableCell>
                <TableCell className="text-sm">
                  {(() => {
                    // Código + nome da cClassTrib (antes mostrava o id interno — USA-CADB-21).
                    const c = cClassTribPorId.get(item.cClassTribId);
                    return c ? <span title={c.nome}>{c.codCClassTrib}</span> : '—';
                  })()}
                </TableCell>
                <TableCell>
                  {item.incidePisCofins
                    ? <Badge variant="default" className="text-xs">Sim</Badge>
                    : <Badge variant="secondary" className="text-xs">Não</Badge>}
                </TableCell>
                <TableCell>
                  {item.incideIpi
                    ? <Badge variant="default" className="text-xs">Sim</Badge>
                    : <Badge variant="secondary" className="text-xs">Não</Badge>}
                </TableCell>
                <TableCell>
                  {item.isMonofasico
                    ? <Badge variant="outline" className="text-xs">Sim</Badge>
                    : null}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => abrirEditar(item.id)} disabled={loading}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setExcluirId(item.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Diálogo principal */}
      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) setDialogOpen(false); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Nova"} Classificação Fiscal</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="identificacao">
            <TabsList className="w-full grid grid-cols-6">
              <TabsTrigger value="identificacao">Identificação</TabsTrigger>
              <TabsTrigger value="cfops">CFOPs</TabsTrigger>
              <TabsTrigger value="pis-ipi">PIS/IPI</TabsTrigger>
              <TabsTrigger value="ibs-cbs">IBS/CBS</TabsTrigger>
              <TabsTrigger value="anp">ANP</TabsTrigger>
              <TabsTrigger value="icms-uf">ICMS por UF</TabsTrigger>
            </TabsList>

            {/* ── Tab 1: Identificação ──────────────────────────────── */}
            <TabsContent value="identificacao" className="space-y-4 pt-3">
              <div className="space-y-1">
                <Label>NCM <span className="text-destructive">*</span></Label>
                <NcmSearchCombobox
                  value={form.ncmId}
                  onChange={(codigoInterno, codigo, descricao) => {
                    set("ncmId", codigoInterno);
                    setNcmLabel(codigo ? `[${codigo}] ${descricao}` : "");
                  }}
                />
                {ncmLabel && <p className="text-xs text-muted-foreground">{ncmLabel}</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-sm">Código Complemento NCM</Label>
                <Input
                  className="h-8 text-sm"
                  value={form.codigoNcmComplemento ?? ""}
                  onChange={e => set("codigoNcmComplemento", e.target.value || null)}
                />
              </div>

              <div className="space-y-1">
                <Label className="text-sm">Classificação Tributária (IBS/CBS) <span className="text-destructive">*</span></Label>
                <CClassTribSearchCombobox
                  value={form.cClassTribId || null}
                  onChange={id => set("cClassTribId", id ?? 0)}
                  placeholder="Selecione"
                  permitirLimpar={false}
                />
              </div>

              <div className="space-y-1">
                <Label className="text-sm">Origem da Mercadoria</Label>
                <Select value={form.origemId?.toString() ?? ""} onValueChange={v => set("origemId", v && v !== "__none__" ? parseInt(v) : null)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Selecione a origem" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {origensList.map(o => (
                      <SelectItem key={o.id} value={o.id.toString()}>{o.codigo} — {o.descricao}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">CST ICMS (Entrada)</Label>
                  <Select value={form.cstId?.toString() ?? ""} onValueChange={v => set("cstId", v && v !== "__none__" ? parseInt(v) : null)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Selecione o CST" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {cstIcmsList.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>{c.cst} — {c.descricao}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">CEST</Label>
                  <CestSearchCombobox value={form.cestId} onChange={v => set("cestId", v)} />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-sm">Observação</Label>
                <Textarea
                  rows={3} className="text-sm resize-none"
                  value={form.observacao ?? ""}
                  onChange={e => set("observacao", e.target.value || null)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                {([
                  ["incidePisCofins", "Incide PIS/COFINS"],
                  ["incideIpi",       "Incide IPI"],
                  ["isMonofasico",    "Monofásico"],
                  ["destacarIcmsST",  "Destaca ICMS ST"],
                  ["produtoEspecifico", "Produto Específico"],
                ] as [keyof SalvarClassificacaoFiscalRequest, string][]).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      id={key}
                      checked={form[key] as boolean}
                      onCheckedChange={v => set(key, v as boolean)}
                    />
                    <Label htmlFor={key} className="text-sm">{label}</Label>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* ── Tab 2: CFOPs ─────────────────────────────────────── */}
            <TabsContent value="cfops" className="pt-3">
              {/* Acelerador: correlação padrão CFOP × CST de saída */}
              <div className="flex items-end gap-2 mb-4 p-3 border rounded-lg bg-muted/30">
                <div className="space-y-1 flex-1 max-w-sm">
                  <Label className="text-xs">Correlação padrão de CFOP × CST de saída</Label>
                  <Select value={cstCorrelacao} onValueChange={setCstCorrelacao}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Selecione o CST de saída" /></SelectTrigger>
                    <SelectContent>
                      {cstIcmsList.map(c => <SelectItem key={c.cst} value={c.cst ?? "?"}>{c.cst} — {c.descricao}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" variant="outline" onClick={preencherCfopsPorCst} disabled={preenchendoCfops || !cstCorrelacao}>
                  {preenchendoCfops ? "Preenchendo..." : "Preencher CFOPs"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Ou preencha manualmente cada CFOP abaixo (deixe vazio para não aplicável).</p>
              <div className="grid grid-cols-3 gap-3">
                <CfopInput label="Saída"                value={form.cfopSaida}             onChange={v => set("cfopSaida", v)} />
                <CfopInput label="Saída Não Contribuinte" value={form.cfopSaidaNaoContrib}  onChange={v => set("cfopSaidaNaoContrib", v)} />
                <CfopInput label="Saída UF"             value={form.cfopSaidaUf}            onChange={v => set("cfopSaidaUf", v)} />
                <CfopInput label="Saída UF Não Contrib." value={form.cfopSaidaUfNaoContrib} onChange={v => set("cfopSaidaUfNaoContrib", v)} />
                <CfopInput label="Saída Exterior"       value={form.cfopSaidaExterior}      onChange={v => set("cfopSaidaExterior", v)} />
                <CfopInput label="Saída Conta p/ Conta" value={form.cfopSaidaContaForaConta} onChange={v => set("cfopSaidaContaForaConta", v)} />
                <CfopInput label="Entrada"              value={form.cfopEntrada}            onChange={v => set("cfopEntrada", v)} />
                <CfopInput label="Entrada UF"           value={form.cfopEntradaUf}          onChange={v => set("cfopEntradaUf", v)} />
                <CfopInput label="Entrada Exterior"     value={form.cfopEntradaExterior}    onChange={v => set("cfopEntradaExterior", v)} />
              </div>
            </TabsContent>

            {/* ── Tab 3: PIS/COFINS e IPI ─────────────────────────── */}
            <TabsContent value="pis-ipi" className="space-y-4 pt-3">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">PIS/COFINS</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">CST PIS/COFINS Entrada</Label>
                    <Select
                      value={form.cstPisCofinsEntradaId?.toString() ?? ""}
                      onValueChange={v => set("cstPisCofinsEntradaId", v && v !== "__none__" ? parseInt(v) : null)}
                    >
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {(cstPisCombo.data ?? []).map(c => (
                          <SelectItem key={c.id} value={c.id.toString()}>{c.cst} — {c.descricao}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">CST PIS/COFINS Saída</Label>
                    <Select
                      value={form.cstPisCofinsaidasId?.toString() ?? ""}
                      onValueChange={v => set("cstPisCofinsaidasId", v && v !== "__none__" ? parseInt(v) : null)}
                    >
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {(cstPisCombo.data ?? []).map(c => (
                          <SelectItem key={c.id} value={c.id.toString()}>{c.cst} — {c.descricao}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Natureza da Receita</Label>
                    <NaturezaReceitaSearchCombobox value={form.naturezaReceitaId} onChange={v => set("naturezaReceitaId", v)} />
                  </div>
                  <Num label="% PIS/COFINS Entrada" value={form.perPisCofinsEntrada} onChange={v => set("perPisCofinsEntrada", v)} />
                  <Num label="% PIS/COFINS Saída"   value={form.perPisCofinsaida}    onChange={v => set("perPisCofinsaida", v)} />
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">IPI</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">CST IPI Entrada</Label>
                    <Select
                      value={form.ipiCstEntradaId?.toString() ?? ""}
                      onValueChange={v => set("ipiCstEntradaId", v && v !== "__none__" ? parseInt(v) : null)}
                    >
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {cstsIpiList.map(c => (
                          <SelectItem key={c.id} value={c.id.toString()}>{c.codigo} — {c.descricao}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">CST IPI Saída</Label>
                    <Select
                      value={form.ipiCstSaidaId?.toString() ?? ""}
                      onValueChange={v => set("ipiCstSaidaId", v && v !== "__none__" ? parseInt(v) : null)}
                    >
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {cstsIpiList.map(c => (
                          <SelectItem key={c.id} value={c.id.toString()}>{c.codigo} — {c.descricao}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Num label="Alíq. IPI (%)" value={form.aliqIpi} onChange={v => set("aliqIpi", v)} />
                  <div className="space-y-1">
                    <Label className="text-xs">Cód. Enquadramento IPI</Label>
                    <Input className="h-8 text-sm" maxLength={3} placeholder="ex.: 999"
                      value={form.codEnquadramentoIpi ?? ""} onChange={e => set("codEnquadramentoIpi", e.target.value || null)} />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ── Tab 4: IBS/CBS ───────────────────────────────────── */}
            <TabsContent value="ibs-cbs" className="pt-3">
              <div className="grid grid-cols-3 gap-3">
                <Num label="Alíq. CBS (%)"           value={form.aliqCbs}           onChange={v => set("aliqCbs", v ?? 0)} />
                <Num label="Alíq. IBS UF (%)"        value={form.aliqIbsUf}         onChange={v => set("aliqIbsUf", v ?? 0)} />
                <Num label="Alíq. IBS Município (%)" value={form.aliqIbsMunicipio}  onChange={v => set("aliqIbsMunicipio", v ?? 0)} />
                <Num label="% Tributos Informado"    value={form.percTributosInformado} onChange={v => set("percTributosInformado", v)} />
              </div>
            </TabsContent>

            {/* ── Tab ANP / Combustível ────────────────────────────── */}
            <TabsContent value="anp" className="space-y-4 pt-3">
              <p className="text-xs text-muted-foreground">Para produtos sujeitos à tributação da ANP (combustíveis e lubrificantes).</p>
              <div className="space-y-1">
                <Label className="text-sm">Código ANP</Label>
                <AnpSearchCombobox
                  value={form.anpId ?? undefined}
                  onChange={(anpId, codigo, descricao) => {
                    set("anpId", anpId);
                    set("codAnp", codigo || null);
                    set("descAnp", descricao || null);
                  }}
                />
                {form.descAnp && <p className="text-xs text-muted-foreground">{form.codAnp} — {form.descAnp}</p>}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">CODIF</Label>
                  <Input className="h-8 text-sm" maxLength={20} value={form.anpCodif ?? ""} onChange={e => set("anpCodif", e.target.value || null)} />
                </div>
                <Num label="Valor de partida (vPart)"      value={form.anpVPart} onChange={v => set("anpVPart", v)} />
                <Num label="% GLGNi (pGNi)"                 value={form.anpPGNi}  onChange={v => set("anpPGNi", v)} />
                <Num label="% GLGNn (pGNn)"                 value={form.anpPGNn}  onChange={v => set("anpPGNn", v)} />
                <Num label="% GLP deriv. petróleo (pGLP)"   value={form.anpPGLP}  onChange={v => set("anpPGLP", v)} />
              </div>
            </TabsContent>

            {/* ── Tab 5: ICMS por UF ───────────────────────────────── */}
            <TabsContent value="icms-uf" className="pt-3 space-y-3">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={abrirUfNovo}>
                  <Plus className="h-4 w-4 mr-1" /> Adicionar UF
                </Button>
              </div>

              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-14">UF</TableHead>
                      <TableHead>Alíq. ICMS</TableHead>
                      <TableHead>Red. BC</TableHead>
                      <TableHead>Alíq. ST</TableHead>
                      <TableHead>MVA ST</TableHead>
                      <TableHead>Destaca</TableHead>
                      <TableHead>Motivo Des.</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.itensUf.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-6 text-sm">
                          Nenhuma UF configurada. Clique em "Adicionar UF" para incluir.
                        </TableCell>
                      </TableRow>
                    )}
                    {form.itensUf.map((uf, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono font-semibold text-sm">{uf.uf}</TableCell>
                        <TableCell className="text-sm">{uf.aliqIcms ?? "—"}</TableCell>
                        <TableCell className="text-sm">{uf.reducaoBcIcms ?? "—"}</TableCell>
                        <TableCell className="text-sm">{uf.aliqIcmsSt ?? "—"}</TableCell>
                        <TableCell className="text-sm">{uf.margemValorSt ?? "—"}</TableCell>
                        <TableCell className="text-sm">{uf.destacarIcms ? "Sim" : "Não"}</TableCell>
                        <TableCell className="text-sm text-xs">
                          {MOTIVOS_DESONERACAO.find(m => m.value === uf.motivoDesoneracao)?.label ?? "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => abrirUfEditar(idx)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removerUf(idx)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSalvar} disabled={loading}>
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo UF */}
      <UfDialog
        open={ufDialogOpen}
        row={ufEditingRow}
        csosnCombo={csosnComboData}
        cstIcmsCombo={cstIcmsList}
        natOpCombo={natOpList}
        onClose={() => setUfDialogOpen(false)}
        onSave={salvarUf}
      />

      {/* Confirmação exclusão */}
      <AlertDialog open={excluirId !== null} onOpenChange={v => { if (!v) setExcluirId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Classificação Fiscal</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza? Esta ação não pode ser desfeita. Os registros por UF vinculados também serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
